import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { firebaseAuthErrorMessage, useAuth } from "@/hooks/useAuth";
import { trackAuthPasswordResetRequest } from "@/lib/analytics";
import { fontStack } from "@/theme";

interface ResetPasswordDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-fills the email field so the user doesn't retype. */
  initialEmail?: string;
  /** Surface identifier for analytics (nav, prompt, welcome). */
  source: string;
}

/**
 * Small dialog: enter email, we send a Firebase password-reset link.
 *
 * Deliberately doesn't disclose whether the address has an account —
 * `sendPasswordResetEmail` doesn't either. Showing a "no account with
 * that email" error would leak account enumeration. So on success or
 * user-not-found we render the same "check your inbox" message.
 */
export default function ResetPasswordDialog({
  open,
  onClose,
  initialEmail,
  source,
}: ResetPasswordDialogProps) {
  const { sendPasswordReset } = useAuth();
  const [email, setEmail] = useState(initialEmail ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEmail(initialEmail ?? "");
      setSent(false);
      setError(null);
      setSubmitting(false);
    }
  }, [open, initialEmail]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const trimmed = email.trim();
    if (!trimmed) {
      setError("Enter an email.");
      return;
    }
    setSubmitting(true);
    setError(null);
    trackAuthPasswordResetRequest(source);
    try {
      await sendPasswordReset(trimmed);
      setSent(true);
    } catch (err) {
      // `auth/user-not-found` → show the same success state; don't leak
      // whether the address exists. Other codes surface the real error.
      const code = String((err as { code?: unknown })?.code ?? "");
      if (code === "auth/user-not-found") {
        setSent(true);
      } else {
        setError(firebaseAuthErrorMessage(err) || "Something went wrong. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: "1rem" }}>Reset password</DialogTitle>
      {sent ? (
        <>
          <DialogContent>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontSize: "0.85rem" }}
            >
              If an account exists for <strong>{email.trim()}</strong>, we've sent a
              password-reset link. Check your inbox.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={onClose}
              variant="contained"
              size="small"
              sx={{ textTransform: "none" }}
            >
              Done
            </Button>
          </DialogActions>
        </>
      ) : (
        <Box component="form" onSubmit={handleSubmit}>
          <DialogContent>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontSize: "0.85rem", mb: 2 }}
            >
              Enter your email and we'll send you a link to reset your password.
            </Typography>
            <TextField
              type="email"
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              disabled={submitting}
              fullWidth
              size="small"
              autoFocus
              required
            />
            {error && (
              <Alert severity="error" sx={{ mt: 1.5, fontSize: "0.85rem", py: 0.5 }}>
                {error}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={onClose}
              size="small"
              sx={{
                color: "text.secondary",
                fontFamily: fontStack.sans,
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="small"
              disabled={submitting}
              startIcon={
                submitting ? <CircularProgress size={14} color="inherit" /> : null
              }
              sx={{ textTransform: "none", fontFamily: fontStack.sans }}
            >
              {submitting ? "…" : "Send reset link"}
            </Button>
          </DialogActions>
        </Box>
      )}
    </Dialog>
  );
}
